import { ImagePaths } from '../../lib/imageUrls.js';

export const conditionalAccessWorkshop = {

    id: 32,
    slug: 'conditional-access-workshop',
    ogImage: '/og/courses/conditional-access-workshop.jpg',
    courseCode: 'CA-WS-001',
    featured: false,
    title: 'Microsoft Entra Conditional Access Workshop',
    subtitle: 'Design and operationalise zero-trust access policies with Microsoft Entra Conditional Access',
    valueProposition: 'A one-day intensive that takes identity administrators from Conditional Access fundamentals to a production-ready policy framework — covering risk-based access, session controls, breakglass design, and change management aligned to ASD Essential Eight and the ACSC Information Security Manual.',
    duration: '8 hours',
    students: 'New',
    level: 'Intermediate',
    image: ImagePaths.COURSE_2,
    shortDescription: 'CA-WS-001 — Microsoft Entra Conditional Access workshop covering zero-trust policy design, risk-based access and breakglass accounts for identity admins.',
    price: {
      aud: 799,
      currency: 'AUD',
      earlyBird: 599
    },
    stripeConfig: {
      productId: 'prod_Uarszg2qB32yHM',
      priceId: 'price_1TikaJKu1nyc322UroFA0XZB'
    },

    snapshot: {
      whoItsFor: [
        'Identity and access administrators designing zero-trust policies',
        'IT managers operationalising Conditional Access at scale',
        'Microsoft 365 and Entra ID engineers tightening tenant security',
        'Security architects aligning identity controls with ASD Essential Eight and the ACSC ISM'
      ],
      accreditation: 'Aligned to Microsoft Learn Conditional Access guidance and ACSC Information Security Manual identity controls'
    },

    logistics: {
      classSize: 'Self-paced online with live Q&A sessions',
      requirements: [
        'Microsoft Entra ID P1 or P2 tenant (trial acceptable) for hands-on labs',
        'Global Reader or Conditional Access Administrator role in a non-production tenant',
        'Working knowledge of Microsoft Entra ID users, groups, and multi-factor authentication',
        'Familiarity with the principles of zero trust and least privilege'
      ]
    },

    overview: 'Microsoft Entra Conditional Access is the policy engine at the heart of a modern zero-trust architecture, evaluating signals from users, devices, locations, and applications to make real-time access decisions. This one-day intensive workshop equips identity administrators and IT managers to plan, deploy, and operate Conditional Access with confidence — moving beyond default templates to a tailored policy framework that meets Australian compliance expectations.\n\nYou will work through Microsoft\'s recommended Conditional Access policy templates, layer in Identity Protection risk signals, configure session controls and app-enforced restrictions, and design the operational practices — report-only mode, breakglass accounts, and change management — that keep your policies safe to evolve. Every module is anchored to the Microsoft Learn Conditional Access planning guide and the ACSC Information Security Manual identity controls so the policies you build are defensible to auditors and resilient under incident.',

    outcomes: [
      'Explain how Conditional Access evaluates signals and enforces grant and session controls',
      'Deploy Microsoft\'s recommended baseline policies including blocking legacy authentication and requiring MFA for administrators',
      'Configure device compliance and Intune integration as a Conditional Access grant control',
      'Use Microsoft Entra ID Protection sign-in and user risk signals to drive risk-based Conditional Access',
      'Apply session controls including sign-in frequency, persistent browser, and app-enforced restrictions',
      'Design breakglass (emergency access) accounts excluded from Conditional Access in line with Microsoft guidance',
      'Operate Conditional Access safely using report-only mode, What If, and structured change management',
      'Map your Conditional Access posture to ASD Essential Eight MFA controls and ACSC ISM identity guidance'
    ],

    audience: [
      'Identity and access administrators',
      'Microsoft 365 and Entra ID engineers',
      'Security architects and cloud security leads',
      'IT managers responsible for tenant security posture',
      'Compliance and risk professionals overseeing identity controls'
    ],

    prerequisites: [
      'Working knowledge of Microsoft Entra ID users, groups, and authentication methods',
      'Familiarity with multi-factor authentication concepts',
      'Access to a Microsoft Entra ID P1 or P2 tenant for hands-on labs',
      'Understanding of zero-trust and least-privilege principles'
    ],

    modules: [
      {
        number: 1,
        title: 'Conditional Access Fundamentals',
        duration: '90 min',
        topics: [
          'Conditional Access as the zero-trust policy engine',
          'Assignments, conditions, and access controls explained',
          'Licensing prerequisites (Entra ID P1, P2, and Microsoft 365 E5)',
          'Policy evaluation order and combination logic',
          'Mapping Conditional Access to the Microsoft Learn planning guide'
        ]
      },
      {
        number: 2,
        title: 'Common Conditional Access Patterns',
        duration: '120 min',
        topics: [
          'Blocking legacy authentication protocols at the tenant edge',
          'Requiring MFA for all users and for administrator roles',
          'Device compliance as a grant control with Microsoft Intune',
          'Location-based policies and trusted named locations',
          'Implementing Microsoft\'s recommended policy templates'
        ]
      },
      {
        number: 3,
        title: 'Risk-Based Conditional Access with Identity Protection',
        duration: '90 min',
        topics: [
          'Microsoft Entra ID Protection sign-in and user risk signals',
          'Configuring risk-based Conditional Access policies',
          'Self-service password reset and secure password change on user risk',
          'Investigating risky users, sign-ins, and risk detections',
          'Tuning risk policies to reduce false positives'
        ]
      },
      {
        number: 4,
        title: 'Session Controls and App-Enforced Restrictions',
        duration: '90 min',
        topics: [
          'Sign-in frequency and persistent browser session controls',
          'App-enforced restrictions for SharePoint Online and Exchange Online',
          'Microsoft Defender for Cloud Apps conditional access app control',
          'Continuous access evaluation (CAE) and token revocation',
          'Protecting unmanaged devices with restricted web sessions'
        ]
      },
      {
        number: 5,
        title: 'Operationalising Conditional Access',
        duration: '90 min',
        topics: [
          'Report-only mode and the What If tool for safe rollout',
          'Designing breakglass (emergency access) accounts per Microsoft guidance',
          'Change management, naming conventions, and policy documentation',
          'Monitoring sign-in logs, audit logs, and Conditional Access insights workbook',
          'Mapping policies to ASD Essential Eight MFA and ACSC ISM identity controls'
        ]
      }
    ],

    format: {
      type: 'One-day intensive workshop with hands-on labs',
      includes: [
        '8 hours of live and self-paced training',
        'Conditional Access policy templates and naming standards',
        'Breakglass account configuration checklist',
        'Report-only rollout runbook',
        'ASD Essential Eight and ACSC ISM mapping worksheet',
        'Certificate of completion'
      ]
    },

    nextIntake: {
      date: 'Register your interest — we will confirm the next cohort',
      details: 'One-day intensive — schedule to suit your team'
    },

    certification: 'Certificate of completion suitable for CPD claims with ACS, ISACA, and (ISC)2',

    faqs: [
      {
        question: 'Do I need Microsoft Entra ID P2 to complete the workshop?',
        answer: 'Microsoft Entra ID P1 covers the core Conditional Access labs. The risk-based Conditional Access module uses Microsoft Entra ID Protection, which requires P2 — a trial tenant is sufficient for the lab exercises.'
      },
      {
        question: 'Does the workshop cover Microsoft\'s recommended policy templates?',
        answer: 'Yes. We walk through the Microsoft Learn Conditional Access recommended policies, including blocking legacy authentication, requiring MFA for administrators, and requiring compliant devices, then adapt them to your tenant.'
      },
      {
        question: 'How does the workshop address breakglass accounts?',
        answer: 'A dedicated section covers designing emergency access accounts that are excluded from Conditional Access, monitored for sign-in activity, and stored securely — following Microsoft guidance and ACSC Information Security Manual identity recommendations.'
      },
      {
        question: 'How does Conditional Access map to ASD Essential Eight?',
        answer: 'Conditional Access is the primary enforcement mechanism for the Essential Eight Multi-Factor Authentication mitigation and supports Restrict Administrative Privileges. The workshop includes a mapping worksheet aligning policies to Maturity Levels One through Three.'
      },
      {
        question: 'Is the workshop suitable for an existing Conditional Access deployment?',
        answer: 'Yes. Many attendees inherit ad-hoc policies and use the workshop to consolidate, rename, and tighten them using report-only mode and the What If tool before retiring legacy policies.'
      },
      {
        question: 'Can I claim the workshop for professional development?',
        answer: 'Yes. The certificate of completion includes the detail required for CPD claims with most Australian professional bodies including ACS, ISACA, and (ISC)2.'
      }
    ],

    australianContext: true,
    complianceFrameworks: [
      'ASD Essential Eight (Multi-Factor Authentication)',
      'ACSC Information Security Manual',
      'OAIC Australian Privacy Principles'
    ]
};
